import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { getErrorType, isHandledError } from '~/types/handled-error';
import { ErrorRetryable } from '~/components/errors/error-retryable';
import * as Sentry from '@sentry/nextjs';
import { ErrorRequireRestart } from '~/components/errors/error-require-restart';

type Props = {
  children: React.ReactNode;
};

/**
 * エラー処理を行うためのErrorBoundaryコンポーネント。
 * エラーは大きく2種類を想定している。
 * - handled
 *   - ロジックでエラーを捕捉した上で、ErrorBoundaryに例外をスローした場合。
 *     - notfound, retryable, require-restartの3種類を想定している。
 * - unhanded
 *   - 予期せぬエラー。Sentryへキャプチャし、リトライ画面を表示する
 * @param children Reactコンポーネントの子要素
 * @returns ErrorBoundaryコンポーネントでラップされたchildren
 */
export const ErrorBoundaryHandler: React.FC<Props> = ({ children }) => {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        if (isHandledError(error)) {
          if (error.options.shouldSendToSentry) {
            Sentry.captureException(error);
          }
          const type = getErrorType(error);
          switch (type) {
            case 'retryable':
              return <ErrorRetryable resetErrorBoundary={resetErrorBoundary} />;
            case 'require-restart':
              return <ErrorRequireRestart />;
            default:
              // unexpected
              return <ErrorRetryable resetErrorBoundary={resetErrorBoundary} />;
          }
        } else {
          Sentry.captureException(error);
          return <ErrorRetryable resetErrorBoundary={resetErrorBoundary} />;
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
